import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { formatDistance } from "@nova/format"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { defineQuery, QueryableResults } from "@nova/queryable"
import { MemberExtended } from "@nova/schemas"
import { LicenseBadge } from "@nova/ds-component-license-badge"
import { selectUserProfileModalContext } from "features/content-overlay/selectors"
import { setUserProfileModalContext } from "features/content-overlay/slice"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useLastOnlineDate } from "features/pilot-tracking/pilots/usePlatformMetrics"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { AddOnIcon } from "plugins/AddonIcon"
import { FunctionComponent, HTMLProps, MouseEventHandler, ReactNode, useMemo } from "react"
import { hideGlobalModal, showGlobalModal } from "runlevel/actions"
import { Spinner } from "themes/default-tailwind"
import * as Icons from "@nova/component-icons"
import { Divider } from "components/Divider"
import { useRouter } from "pages"
import { ButtonTertiary } from "@nova/component-btn"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"

export function UserProfileModalContainer() {
    const dispatch = useAppDispatch()
    const { userId } = useAppSelector(selectUserProfileModalContext)
    const user = useAppSelector(selectWorkspaceMembers).find((e) => e.userId === userId)
    const { stats, isLoading: isStatsLoading } = useUserFlightStats(user?.id)

    if (!userId) return null

    return (
        <HighPriorityNovaModalContainer
            title="Profile"
            closeModal={() => dispatch(hideGlobalModal())}
        >
            {isStatsLoading || !user ? <Spinner /> : <UserProfileModal user={user} stats={stats} />}
        </HighPriorityNovaModalContainer>
    )
}

export const UserProfileModal = ({
    user,
    stats,
}: {
    user: MemberExtended
    stats: UserFlightStats | undefined
}) => {
    const router = useRouter("/workspace/:workspaceId")
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const { formattedLastOnlineString } = useLastOnlineDate({ pilotId: user.id! })

    return (
        <div className="flex flex-col gap-4 min-w-[600px] pt-2">
            <div className="flex gap-8">
                <div className="w-32 h-32 aspect-square rounded-2xl overflow-hidden">
                    <ProfilePhoto userId={user.id!} fullName={user.fullName ?? ""} />
                </div>

                <div className="flex flex-col gap-4 flex-1">
                    <div className="flex flex-col">
                        <small className="text-gray-300">{user.organizationName}</small>
                        <h2 className="text-[24px] text-gray-0 telegraf mt-1">{user.fullName}</h2>
                        <h4 className="text-gray-100 mt-0.5">{user.title}</h4>
                    </div>

                    <div className="flex gap-3">
                        <LicenseBadge license={user.license.subscriptionType} />
                        <div className="flex items-center gap-2">
                            {Object.values(user.addOns).map(
                                (addOn) =>
                                    addOn?.subscriptionType && (
                                        <AddOnIcon
                                            key={addOn.subscriptionId}
                                            addOn={addOn.subscriptionType}
                                        />
                                    )
                            )}
                        </div>
                    </div>

                    {(user.phoneNumber || user.email) && (
                        <div className="flex flex-col gap-1">
                            {user.phoneNumber && (
                                <div className="flex gap-2 items-center">
                                    <Icons.SmallIcon Icon={Icons.Call} className="text-gray-500" />
                                    <small className="text-gray-300">{user.phoneNumber}</small>
                                </div>
                            )}

                            {user.email && (
                                <div className="flex gap-2 items-center">
                                    <Icons.SmallIcon Icon={Icons.Mail} className="text-gray-500" />
                                    <small className="text-gray-300">{user.email}</small>
                                </div>
                            )}
                        </div>
                    )}

                    {stats && <Divider />}

                    {stats && (
                        <div className="flex">
                            <div className="flex flex-col flex-1 gap-4">
                                <Icons.SmallIcon Icon={Icons.Drone} className="text-gray-500" />
                                <div className="flex flex-col">
                                    <h3>{stats.totalFlights}</h3>
                                    <small className="text-gray-300">Flights</small>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 gap-4">
                                <Icons.SmallIcon
                                    Icon={Icons.Controller}
                                    className="text-gray-500"
                                />

                                <div className="flex flex-col">
                                    <h3>{stats.totalHours?.toFixed(0)} hr</h3>
                                    <small className="text-gray-300">Flight Time</small>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 gap-4">
                                <Icons.SmallIcon Icon={Icons.Distance} className="text-gray-500" />

                                <div className="flex flex-col">
                                    <h3>
                                        {formatDistance({
                                            value: stats.totalDistanceMeters,
                                            unit: distanceUnit,
                                        })}
                                    </h3>
                                    <small className="text-gray-300">Distance Flown</small>
                                </div>
                            </div>
                        </div>
                    )}

                    <Divider />

                    <div className="flex justify-between">
                        <div className="flex gap-1 text-gray-400">
                            <small>Last online</small>
                            <small>{formattedLastOnlineString ?? "--"}</small>
                        </div>
                        <div className="flex gap-1 text-gray-400">
                            <small>Joined on</small>
                            <small>
                                {user.createdAt ? new Date(user.createdAt).toDateString() : "--"}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {stats && (
                <router.Link to={{ path: "/fleet/pilots/:pilotId", params: { pilotId: user.id! } }}>
                    <ButtonTertiary
                        title="Visit Pilot Profile"
                        className="w-full"
                        LeftIcon={Icons.PersonBook}
                    />
                </router.Link>
            )}
        </div>
    )
}

type UserProfileModalTriggerProps = {
    userId: string | undefined
    children: ReactNode
    className?: string
}

export const UserProfileModalTrigger: FunctionComponent<
    UserProfileModalTriggerProps & HTMLProps<HTMLButtonElement>
> = ({ userId, children, className, onClick, ...rest }) => {
    const dispatch = useAppDispatch()

    const user = useAppSelector(selectWorkspaceMembers).find((e) => e.userId === userId)

    if (!userId || !user || !user.fullName) {
        return <>{children}</>
    }

    const open: MouseEventHandler = (e) => {
        e.stopPropagation()
        dispatch(setUserProfileModalContext({ userId }))
        dispatch(showGlobalModal())
    }

    return (
        <button
            {...rest}
            type="button"
            className={`group/UserProfileModalTrigger block ${className ?? ""}`}
            onClick={(e) => {
                open(e)
                onClick?.(e)
            }}
        >
            {children}
        </button>
    )
}

const flightsQuery = defineQuery((userId: string) => ({
    resource: "metadata",
    aggregates: [{ field: "mission_id", func: "COUNT", alias: "totalFlights" }],
    filters: [{ field: "mission.user_id", op: "=", value: userId }],
}))

const hoursQuery = defineQuery((userId: string) => ({
    resource: "metadata",
    aggregates: [{ field: "airtime_hours", func: "SUM", alias: "totalHours" }],
    filters: [{ field: "mission.user_id", op: "=", value: userId }],
}))

const distanceQuery = defineQuery((userId: string) => ({
    resource: "metadata",
    aggregates: [{ field: "distance_flown", func: "SUM", alias: "totalDistance" }],
    filters: [{ field: "mission.user_id", op: "=", value: userId }],
}))

type UserFlightStats = Pick<QueryableResults<typeof flightsQuery>[number], "totalFlights"> &
    Pick<QueryableResults<typeof hoursQuery>[number], "totalHours"> & {
        totalDistanceMeters: number
    }

const useUserFlightStats = (userId: string | undefined) => {
    const queries = useMemo(
        () => ({
            totalFlights: flightsQuery(userId ?? ""),
            totalHours: hoursQuery(userId ?? ""),
            totalDistance: distanceQuery(userId ?? ""),
        }),
        [userId]
    )

    const { data, isLoading } = useBatchPilotTrackingQuery(queries)

    if (!data) return { stats: undefined, isLoading }
    const totalFlights = data.totalFlights[0]?.totalFlights ?? 0
    if (totalFlights === 0) return { stats: undefined, isLoading }

    return {
        stats: {
            totalFlights,
            totalHours: data.totalHours[0]?.totalHours ?? 0,
            totalDistanceMeters: data.totalDistance[0]?.totalDistance ?? 0,
        },
        isLoading,
    }
}
